import { z } from 'zod';
import { ControlVariableDefinition } from 'libs/end-to-end/src/types/databases/controls/control-variable';
import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { FILTER_SET_DEFAULT_NAME } from 'libs/end-to-end/src/types/databases/filters/filter-definition';

export const DataIntelligenceDeepDiveToolsDefinition = z
  .object({
    /**
     * The name of the filter set to use for the "data intelligence, deep dive" section of the focus area
     */
    filterSetName: z.string().optional(),
  })
  .describe('DataIntelligenceDeepDiveToolsDefinition');

export type DataIntelligenceDeepDiveToolsDefinition = z.infer<
  typeof DataIntelligenceDeepDiveToolsDefinition
>;

export const BinScatterMultiSelectToolsDefinition = z
  .object({
    /**
     * The name of the filter set to use for the "bin scatter multi select" section of the focus area
     */
    filterSetName: z.string().optional(),
  })
  .describe('BinScatterMultiSelectToolsDefinition');

export type BinScatterMultiSelectToolsDefinition = z.infer<
  typeof BinScatterMultiSelectToolsDefinition
>;

export const FocusAreaToolsDefinition = z
  .object({
    /**
     * The settings to use for the "data intelligence, deep dive" section of the focus area.
     */
    dataIntelligenceDeepDiveDefinition:
      DataIntelligenceDeepDiveToolsDefinition.optional(),

    /**
     * The settings to use for the "bin scatter multi select" section of the focus area.
     */
    binScatterMultiSelectDefinition:
      BinScatterMultiSelectToolsDefinition.optional(),
  })
  .describe('FocusAreaToolsDefinition');

export type FocusAreaToolsDefinition = z.infer<typeof FocusAreaToolsDefinition>;

export const inferFullToolsDefinitionWithDefaults = (
  toolsDefinition: FocusAreaToolsDefinition | undefined,
): FocusAreaToolsDefinition => {
  return {
    dataIntelligenceDeepDiveDefinition: {
      filterSetName:
        toolsDefinition?.dataIntelligenceDeepDiveDefinition?.filterSetName ??
        FILTER_SET_DEFAULT_NAME,
    },
    binScatterMultiSelectDefinition: {
      filterSetName:
        toolsDefinition?.binScatterMultiSelectDefinition?.filterSetName ??
        FILTER_SET_DEFAULT_NAME,
    },
  };
};

export const FocusAreaDefinitionIconConfiguration = z
  .enum([
    'hiring',
    'compensation',
    'promotion',
    'performance',
    'engagement',
    'leadership',
    'attrition',
    'search',
    'target',
    'arrests',
  ])
  .describe('FocusAreaDefinitionIconConfiguration');

export type FocusAreaDefinitionIconConfiguration = z.infer<
  typeof FocusAreaDefinitionIconConfiguration
>;

export const FocusAreaDefinition = z
  .object({
    focusArea: FocusArea,
    displayName: z.string().optional(),
    controlVariables: z.array(ControlVariableDefinition),
    metrics: z.array(MetricDefinition),

    /**
     * The tools definition for the focus area.
     */
    toolsDefinition: FocusAreaToolsDefinition.optional(),

    /**
     * Whether to hide the focus area on the UI.
     * We use this for use cases where the intelligence features are "disabled", but in reality we still perform their analysis/etc.
     */
    isHidden: z.boolean().optional(),
    iconConfiguration: FocusAreaDefinitionIconConfiguration.optional(),
  })
  .describe('FocusAreaDefinition');

export type FocusAreaDefinition = z.infer<typeof FocusAreaDefinition>;
